import { useCallback, useEffect, useState } from 'react'
import { FileTreeNode, buildFileTreeFromPaths, toggleNodeExpanded } from './fileTreeUtils'
import { FileTreeItem } from './FileTreeItem'

export interface FileItem {
  /** The file path to display in the tree */
  filePath: string
  /** Optional associated data (e.g., Diff, CanvasData) */
  data?: unknown
}

interface FileTreeProps {
  /** Array of files to display in the tree */
  files: FileItem[]
  /** Currently selected file path */
  selectedPath: string | null
  /** Callback when a file is selected */
  onFileSelect: (path: string, data?: unknown) => void
  /** Optional empty state message */
  emptyMessage?: string
}

export function FileTree({
  files,
  selectedPath,
  onFileSelect,
  emptyMessage = 'No files to display'
}: FileTreeProps) {
  const [treeRoot, setTreeRoot] = useState<FileTreeNode | null>(null)

  // Build tree when files change
  useEffect(() => {
    if (files && files.length > 0) {
      const root = buildFileTreeFromPaths(files)
      setTreeRoot(root)
    } else {
      setTreeRoot(null)
    }
  }, [files])

  const handleFolderToggle = useCallback(
    (path: string) => {
      if (treeRoot) {
        const updatedTree = toggleNodeExpanded(treeRoot, path)
        setTreeRoot(updatedTree)
      }
    },
    [treeRoot]
  )

  const handleFileClick = useCallback(
    (path: string, data?: unknown) => {
      onFileSelect(path, data)
    },
    [onFileSelect]
  )

  if (!treeRoot || !treeRoot.children || treeRoot.children.length === 0) {
    return <div className='p-4 text-[rgba(0,0,0,0.45)] text-[14px]'>{emptyMessage}</div>
  }

  return (
    <div className='box-border content-stretch flex flex-col gap-[8px] items-start px-[16px] py-0 relative shrink-0 w-full'>
      {treeRoot.children.map((child, index) => (
        <FileTreeItem
          key={`${child.path}-${index}`}
          node={child}
          level={0}
          selectedPath={selectedPath}
          onFileClick={handleFileClick}
          onFolderToggle={handleFolderToggle}
        />
      ))}
    </div>
  )
}
